import { getSuiteBySlug } from '@/features/suites/data/suites';
import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import { mapQueryResult, mapSuiteRunDto } from '@/shared/data/dtoMappers';
import { getErrorMessage } from '@/shared/data/errors';
import { buildEqFilter, mergeFilters, type GridFilters } from '@/ui/composites/grid/gridFilters';
import type {
  QueryFilter,
  QueryResult,
  QuerySort,
  SuiteRunDto,
  SuiteRunStatusSummaryDto,
} from '@flowqa/shared';

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (): string => {
  const token = getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

const suiteRunSortFieldMap: Readonly<Record<string, string>> = {
  runNumber: 'timestamp',
};

const mapSuiteRunSorts = (sorts: QuerySort[]): QuerySort[] => {
  const mappedSorts: QuerySort[] = [];
  for (const sort of sorts) {
    const field = typeof sort.field === 'string' ? sort.field : String(sort.field);
    const direction = sort.direction === 'desc' ? 'desc' : 'asc';
    const mappedField = Object.prototype.hasOwnProperty.call(suiteRunSortFieldMap, field)
      ? suiteRunSortFieldMap[field]
      : field;
    mappedSorts.push({ field: mappedField, direction });
  }
  return mappedSorts;
};

export const querySuiteRuns = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: QueryFilter[];
    search?: string;
    include?: SuiteRunInclude;
    includeSummary?: boolean;
  },
): Promise<QueryResult<SuiteRunDto>> => {
  const token = ensureToken();
  const filters: GridFilters = input?.filters ?? [];
  const hasProjectFilter = filters.some((filter) => filter.field === 'projectId');
  const projectFilters =
    !hasProjectFilter && projectId ? buildEqFilter('projectId', projectId) : [];
  const combinedFilters = mergeFilters(projectFilters, filters);
  const sorts = input?.sorts
    ? mapSuiteRunSorts(input.sorts)
    : [{ field: 'timestamp', direction: 'desc' }];
  try {
    const result = await apiClient.post<QueryResult<SuiteRunDto>>(
      'suite-runs/query',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 50,
        sorts,
        filters: combinedFilters,
        search: input?.search,
        include: input?.include,
        includeSummary: input?.includeSummary,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
    return mapQueryResult(result, mapSuiteRunDto);
  } catch (err) {
    throw toApiError(err);
  }
};

export const querySuiteRunStatusSummary = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: QueryFilter[];
    include?: SuiteRunInclude;
  },
): Promise<SuiteRunStatusSummaryDto> => {
  const token = ensureToken();
  const filters: GridFilters = input?.filters ?? [];
  const hasProjectFilter = filters.some((filter) => filter.field === 'projectId');
  const projectFilters =
    !hasProjectFilter && projectId ? buildEqFilter('projectId', projectId) : [];
  const combinedFilters = mergeFilters(projectFilters, filters);
  const sorts = input?.sorts ? mapSuiteRunSorts(input.sorts) : [];
  try {
    return await apiClient.post<SuiteRunStatusSummaryDto>(
      'suite-runs/summary',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 1,
        sorts,
        filters: combinedFilters,
        include: input?.include,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const getSuiteRunById = async (
  id: string,
  organisationId?: string | null,
): Promise<SuiteRunDto> => {
  const token = ensureToken();
  try {
    const result = await apiClient.get<SuiteRunDto>(`suite-runs/${id}`, {
      retries: 1,
      orgId: organisationId ?? undefined,
      token,
    });
    return mapSuiteRunDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const getSuiteRunByRunNumber = async (
  suiteSlug: string | null,
  runNumber: number | null,
  projectId?: string | null,
  organisationId?: string | null,
): Promise<SuiteRunDto | null> => {
  if (!suiteSlug || runNumber === null || !Number.isFinite(runNumber) || runNumber < 1) {
    return null;
  }
  let suite = await getSuiteBySlug(suiteSlug, projectId, organisationId);
  if (!suite && projectId) {
    suite = await getSuiteBySlug(suiteSlug, null, organisationId);
  }
  if (!suite) {
    return null;
  }
  const resolvedProjectId = suite.projectId;
  const filters = buildEqFilter('suiteId', suite.id);
  const pageSize = 100;
  const runSorts = [
    { field: 'timestamp', direction: 'asc' },
    { field: 'id', direction: 'asc' },
  ] satisfies QuerySort[];
  const firstPage = await querySuiteRuns(resolvedProjectId, organisationId, {
    page: 1,
    pageSize,
    sorts: runSorts,
    filters,
    include: { resultsFile: true, resultsFileRunGroup: true, suite: true, project: true },
  });
  const firstMatch = firstPage.items.find((item) => item.runNumber === runNumber);
  if (firstMatch) return firstMatch;
  const maxPage = Math.max(1, Math.ceil(firstPage.total / pageSize));
  if (maxPage === 1) return null;
  const targetPage = Math.min(maxPage, Math.max(2, Math.ceil(runNumber / pageSize)));
  if (targetPage !== 1) {
    const targetResult = await querySuiteRuns(resolvedProjectId, organisationId, {
      page: targetPage,
      pageSize,
      sorts: runSorts,
      filters,
      include: { resultsFile: true, resultsFileRunGroup: true, suite: true, project: true },
    });
    const targetMatch = targetResult.items.find((item) => item.runNumber === runNumber);
    if (targetMatch) return targetMatch;
  }
  for (let page = 2; page <= maxPage; page += 1) {
    if (page === targetPage) continue;
    const result = await querySuiteRuns(resolvedProjectId, organisationId, {
      page,
      pageSize,
      sorts: runSorts,
      filters,
      include: { resultsFile: true, resultsFileRunGroup: true, suite: true, project: true },
    });
    const match = result.items.find((item) => item.runNumber === runNumber);
    if (match) return match;
    if (result.items.length < pageSize) break;
  }
  return null;
};

export const createSuiteRun = async (
  input: {
    runId: string;
    suiteId: string;
    status?: SuiteRunDto['status'];
    timestamp?: Date | string | null;
    name?: string | null;
    classname?: string | null;
    file?: string | null;
    durationMs?: number | null;
    tests?: number | null;
    failures?: number | null;
    errors?: number | null;
    skipped?: number | null;
    systemOut?: string | null;
    systemErr?: string | null;
  },
  organisationId?: string | null,
): Promise<SuiteRunDto> => {
  const token = ensureToken();
  const body = {
    runId: input.runId.trim(),
    suiteId: input.suiteId.trim(),
    status: input.status,
    timestamp: input.timestamp ?? undefined,
    name: input.name ?? undefined,
    classname: input.classname ?? undefined,
    file: input.file ?? undefined,
    durationMs: input.durationMs ?? undefined,
    tests: input.tests ?? undefined,
    failures: input.failures ?? undefined,
    errors: input.errors ?? undefined,
    skipped: input.skipped ?? undefined,
    systemOut: input.systemOut ?? undefined,
    systemErr: input.systemErr ?? undefined,
  };
  try {
    const result = await apiClient.post<SuiteRunDto>('suite-runs', body, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapSuiteRunDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const updateSuiteRun = async (
  input: {
    id: string;
    tags?: string[];
    status?: SuiteRunDto['status'];
    timestamp?: Date | string | null;
    name?: string | null;
    classname?: string | null;
    file?: string | null;
    durationMs?: number | null;
    tests?: number | null;
    failures?: number | null;
    errors?: number | null;
    skipped?: number | null;
    systemOut?: string | null;
    systemErr?: string | null;
  },
  organisationId?: string | null,
): Promise<SuiteRunDto> => {
  const token = ensureToken();
  const body = {
    tags: input.tags,
    status: input.status,
    timestamp: input.timestamp ?? undefined,
    name: input.name ?? undefined,
    classname: input.classname ?? undefined,
    file: input.file ?? undefined,
    durationMs: input.durationMs ?? undefined,
    tests: input.tests ?? undefined,
    failures: input.failures ?? undefined,
    errors: input.errors ?? undefined,
    skipped: input.skipped ?? undefined,
    systemOut: input.systemOut ?? undefined,
    systemErr: input.systemErr ?? undefined,
  };
  try {
    const result = await apiClient.patch<SuiteRunDto>(`suite-runs/${input.id}`, body, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapSuiteRunDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export type SuiteRunInclude = {
  suite?: boolean;
  project?: boolean;
  run?: boolean;
  resultsFile?: boolean;
  resultsFileCreatedBy?: boolean;
  resultsFileRunGroup?: boolean;
};

export const deleteSuiteRun = async (
  input: { id: string },
  organisationId?: string | null,
): Promise<{ id: string }> => {
  const token = ensureToken();
  try {
    await apiClient.post<undefined>(`suite-runs/${input.id}/delete`, undefined, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};

export const restoreSuiteRun = async (
  input: { id: string },
  organisationId?: string | null,
): Promise<{ id: string }> => {
  const token = ensureToken();
  try {
    await apiClient.post<undefined>(`suite-runs/${input.id}/restore`, undefined, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};
